import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  BadgeCheck,
  Bot,
  CalendarClock,
  ExternalLink,
  FileSearch,
  FileText,
  Landmark,
  LoaderCircle,
  Scale,
} from "lucide-react";
import { AppLink, FeedbackBanner, useRouter } from "@tribunalea/ui";
import { useApi } from "../App.jsx";
import {
  CatalogPagination,
  CopyCanonicalLink,
  formatLegalDate,
  navigateBackToCatalog,
  preservedCatalogHistoryState,
} from "../components/LegalCatalogUi.jsx";
import { isValidDateOnly } from "../lib/date-only.js";
import { usePageMetadata } from "../lib/use-page-metadata.js";

const CONTENT_LIMIT = 50;

function detailRoute(search, pageNumber = null) {
  const parameters = new URLSearchParams(search);
  const pageValue = Number(parameters.get("page") || 1);
  const asOfDate = parameters.get("asOf") || "";
  return {
    page: pageNumber || (Number.isInteger(pageValue) && pageValue > 0 ? pageValue : 1),
    asOfDate: isValidDateOnly(asOfDate) ? asOfDate : "",
  };
}

function buildDetailUrl(path, { asOfDate = "" }) {
  const parameters = new URLSearchParams();
  if (asOfDate) parameters.set("asOf", asOfDate);
  const serialized = parameters.toString();
  return `${path}${serialized ? `?${serialized}` : ""}`;
}

function openJustiniano(prompt) {
  window.dispatchEvent(new CustomEvent("justiniano:compose", {
    detail: { appId: "instrumenta", prompt },
  }));
}

export function NormDetailPage({ slug, pageNumber = null, unitSlug = null }) {
  const api = useApi();
  const { path, search, navigate } = useRouter();
  const route = useMemo(() => detailRoute(search, pageNumber), [pageNumber, search]);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api.getNorm(slug, {
      asOfDate: route.asOfDate,
      page: route.page,
      limit: CONTENT_LIMIT,
      unitSlug,
    }).then((data) => {
      if (!active) return;
      const canonicalPath = data.requestedUnit?.url || data.pageUrl;
      const canonicalUrl = buildDetailUrl(canonicalPath, route);
      if (`${path.replace(/\/$/, "")}${search}` !== canonicalUrl) {
        navigate(canonicalUrl, {
          replace: true,
          state: preservedCatalogHistoryState(),
        });
        return;
      }
      setRecord(data);
    }).catch((requestError) => {
      if (!active) return;
      setRecord(null);
      setError(requestError.message);
    }).finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [api, navigate, path, route, search, slug, unitSlug]);

  useEffect(() => {
    if (!record?.requestedUnit?.id) return undefined;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(`unidad-${record.requestedUnit.id}`)?.scrollIntoView({
        block: "center",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [record?.requestedUnit?.id]);

  usePageMetadata({
    title: record ? `${record.shortName || record.name} · Normas · Instrumenta` : null,
    description: record
      ? `${record.name}. ${record.jurisdiction.name}. ${record.statusLabel}.`
      : null,
    canonicalPath: record?.requestedUnit?.url || record?.pageUrl,
  });

  if (loading) {
    return (
      <div className="page legal-resource-page">
        <section className="legal-resource-loading">
          <LoaderCircle className="spin" size={23} /> Abriendo la norma…
        </section>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="page legal-resource-page">
        <button
          className="back-link"
          type="button"
          onClick={() => navigateBackToCatalog(navigate, "/normas")}
        >
          <ArrowLeft size={17} /> Volver al catálogo
        </button>
        <FeedbackBanner>{error || "No encontramos la norma solicitada."}</FeedbackBanner>
      </div>
    );
  }

  const goToPage = (page) => navigate(buildDetailUrl(
    `/normas/${record.slug}/pagina/${page}`,
    route,
  ), { replace: true, state: preservedCatalogHistoryState() });
  const currentSourcePath = record.requestedUnit?.url || record.pageUrl;

  return (
    <div className="page legal-resource-page">
      <button
        className="back-link"
        type="button"
        onClick={() => navigateBackToCatalog(navigate, "/normas")}
      >
        <ArrowLeft size={17} /> Volver al catálogo de normas
      </button>

      <article className="legal-resource-header">
        <div className="legal-resource-heading-icon"><Scale size={25} /></div>
        <div className="legal-resource-heading-copy">
          <div className="legal-resource-kicker">
            <span className="eyebrow">{record.typeLabel} · {record.jurisdiction.name}</span>
            <span className={`norm-status is-${record.status}`}>{record.statusLabel}</span>
          </div>
          <h1>{record.shortName || record.name}</h1>
          {record.shortName && record.shortName !== record.name && <p>{record.name}</p>}
          <div className="legal-resource-meta">
            <span><Landmark size={15} /> {record.jurisdiction.name}</span>
            {record.number && <span><FileText size={15} /> N.º {record.number}</span>}
            {record.source.official && <span><BadgeCheck size={15} /> Fuente oficial</span>}
          </div>
        </div>
        <div className="legal-resource-actions">
          <CopyCanonicalLink path={currentSourcePath} />
          {record.source.url && (
            <a className="secondary-button" href={record.source.url} target="_blank" rel="noreferrer">
              <ExternalLink size={16} /> Fuente original externa
            </a>
          )}
          <button
            className="primary-button"
            type="button"
            onClick={() => openJustiniano(
              `Explicá esta norma y citá únicamente sus artículos relevantes: ${record.citation}. Fuente Tribunalea: ${new URL(currentSourcePath, window.location.origin)}`,
            )}
          >
            <Bot size={16} /> Consultar con Justiniano
          </button>
        </div>
      </article>

      {record.summary && <p className="legal-resource-lead">{record.summary}</p>}

      <section className="legal-resource-summary-grid">
        <div><span>Estado</span><strong>{record.statusLabel}</strong></div>
        <div><span>Vigencia desde</span><strong>{formatLegalDate(record.effectiveFrom)}</strong></div>
        <div><span>Publicación</span><strong>{formatLegalDate(record.publishedAt)}</strong></div>
      </section>

      <section className="legal-resource-content">
        <header className="with-filter">
          <div>
            <span className="eyebrow">Texto normativo</span>
            <h2>Artículos y unidades</h2>
          </div>
          <label className="legal-as-of-filter">
            <CalendarClock size={15} />
            <span>Texto vigente al</span>
            <input
              type="date"
              value={route.asOfDate}
              onChange={(event) => navigate(buildDetailUrl(
                record.requestedUnit?.url || `/normas/${record.slug}/pagina/1`, {
                asOfDate: event.target.value,
              }), { replace: true, state: preservedCatalogHistoryState() })}
            />
          </label>
        </header>
        {record.units.length ? (
          <div className="legal-resource-sections norm-sections">
            {record.units.map((unit) => (
              <article
                key={unit.id}
                id={`unidad-${unit.id}`}
                className={record.requestedUnit?.id === unit.id ? "is-direct-target" : ""}
              >
                <div className="legal-section-heading">
                  <span className="legal-section-reference">{unit.reference}</span>
                  <div className="legal-section-sources">
                    {unit.official && <span className="official-source"><BadgeCheck size={12} /> Oficial</span>}
                    <AppLink className="legal-direct-source" to={unit.url}>
                      Abrir fuente Tribunalea <ExternalLink size={12} />
                    </AppLink>
                    {unit.source.url && (
                      <a className="legal-original-source" href={unit.source.url} target="_blank" rel="noreferrer">
                        Fuente original externa <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </div>
                {unit.heading && <h3>{unit.heading}</h3>}
                <p>{unit.text}</p>
              </article>
            ))}
          </div>
        ) : (
          <section className="legal-catalog-empty compact">
            <FileSearch size={24} />
            <h2>No hay texto disponible para esta fecha</h2>
            <p>Probá sin fecha de vigencia o consultá la fuente identificada.</p>
          </section>
        )}
        <CatalogPagination
          pagination={record.pagination}
          onPage={goToPage}
          itemLabel="unidades"
          ariaLabel="Páginas del texto normativo"
        />
      </section>
    </div>
  );
}
